import { useState, useEffect, useCallback } from 'react';
import { api, MarketplaceArche, MarketplaceSearchResult, ArcheType } from '../../lib/api';
import { traceStart, traceEnd, traceError, addBreadcrumb } from '../../lib/telemetry';

interface UseMarketplaceSearchOptions {
  query?: string;
  categoryId?: string;
  archeType?: ArcheType;
  tags?: string[];
  sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
  limit?: number;
}

interface UseMarketplaceSearchReturn {
  results: MarketplaceSearchResult | null;
  loading: boolean;
  error: string | null;
  arches: MarketplaceArche[];
  total: number;
  hasMore: boolean;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  refresh: () => void;
}

export function useMarketplaceSearch(
  options: UseMarketplaceSearchOptions = {}
): UseMarketplaceSearchReturn {
  const { query, categoryId, archeType, tags, sortBy, limit = 20 } = options;

  const [results, setResults] = useState<MarketplaceSearchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const offset = (page - 1) * limit;

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);

    const searchParams = { q: query, categoryId, archeType, tags, sortBy, limit, offset };
    const traceId = traceStart('marketplaceSearch', {
      searchParams,
      apiBase: import.meta.env.VITE_API_URL || '/v1',
    });

    addBreadcrumb('marketplace', 'Starting search', searchParams);

    try {
      const result = await api.searchMarketplace(searchParams);

      // Log detailed result info for debugging
      addBreadcrumb('marketplace', `Search returned ${result.arches.length} arches`, {
        total: result.total,
        hasMore: result.hasMore,
        archeIds: result.arches.slice(0, 5).map(a => a.archId),
      });

      traceEnd(traceId, {
        archeCount: result.arches.length,
        total: result.total,
        hasMore: result.hasMore,
      });

      setResults(result);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to search';
      traceError(traceId, err instanceof Error ? err : new Error(String(err)), {
        searchParams,
      });
      addBreadcrumb('marketplace', `Search failed: ${errorMsg}`);
      setError(errorMsg);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [query, categoryId, archeType, tags, sortBy, limit, offset]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Reset to page 1 when search parameters change
  useEffect(() => {
    setPage(1);
  }, [query, categoryId, archeType, tags, sortBy]);

  const arches = results?.arches ?? [];
  const total = results?.total ?? 0;
  const hasMore = results?.hasMore ?? false;
  const totalPages = Math.ceil(total / limit);

  return {
    results,
    loading,
    error,
    arches,
    total,
    hasMore,
    page,
    setPage,
    totalPages,
    refresh: fetchResults,
  };
}
